/** Parse Legend Sovereign HTML into sidebar tabs the operator UI can render neatly. */

export const PLAYBOOK_FIELD_LABELS = [
  'Playbook Title',
  'Approach Angle',
  'Opening Move',
  'Anticipated Responses',
  'Response A',
  'Adaptation A',
  'Response B',
  'Adaptation B',
  'DISC Language and Tone',
  'Escalation Path',
  'Fallback',
  'Execution Steps',
  'Source Evidence',
  'Pretext Title',
  'Pretext Type + Delivery Channel',
  'Informational Goal',
  'Role Profile',
  'Elicitor Objective',
  'Elicitation Pathway',
  'Information Categories at Risk',
  'Expected Mission Impact',
  'Warning Signs',
  'Detection Opportunities',
  'Defensive Response',
  'Severity to Elicitor',
  'Confidence of Success',
  'Source Basis',
];

export const DISC_SECTION_HEADINGS = [
  'DISC RANK',
  'WHY THIS RANKING',
  'COMMUNICATION TIPS',
  'WHAT THIS PROFILE MEANS',
  'STRENGTHS',
  'WEAKNESSES',
  'HOW TO COMMUNICATE',
  'DISC PRIMARY STYLE',
  'DISC SECONDARY STYLE',
  'EVIDENCE FROM THE DUMP',
  'HOW THIS SHAPES ELICITATION',
  'OPERATIONAL CAUTIONS',
  'SPEECH AND WRITING PATTERNS',
  'RECOMMENDED ELICITOR APPROACH',
];

const DISC_HEADING_ALIASES = {
  'DISC RANK': 'DISC rank',
  'WHY THIS RANKING': 'Why this ranking',
  'COMMUNICATION TIPS': 'Communication tips',
  'WHAT THIS PROFILE MEANS': 'Why this ranking',
  'STRENGTHS': 'Why this ranking',
  'WEAKNESSES': 'Why this ranking',
  'HOW TO COMMUNICATE': 'Communication tips',
  'DISC PRIMARY STYLE': 'DISC primary style',
  'DISC SECONDARY STYLE': 'DISC secondary style',
  'EVIDENCE FROM THE DUMP': 'Why this ranking',
  'HOW THIS SHAPES ELICITATION': 'Communication tips',
  'OPERATIONAL CAUTIONS': 'Communication tips',
  'SPEECH AND WRITING PATTERNS': 'Why this ranking',
  'RECOMMENDED ELICITOR APPROACH': 'Communication tips',
};

function escapeRegExp(value) {
  return String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function decodeEntities(text) {
  return String(text || '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"');
}

export function htmlFragmentToPlain(html) {
  const raw = String(html || '');
  if (!raw.trim()) return '';
  const withBreaks = raw
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|h[1-6]|li|tr|section|pre|blockquote)>/gi, '\n\n')
    .replace(/<(p|div|h[1-6]|li|section|blockquote)[^>]*>/gi, '\n')
    .replace(/<\/t[dh]>/gi, '\t')
    .replace(/<li>/gi, '\n- ')
    .replace(/<[^>]+>/g, '');
  return decodeEntities(withBreaks).replace(/\r\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
}

export function splitLabeledFields(text, labels) {
  const prepared = String(text || '').replace(/\r\n/g, '\n');
  if (!prepared.trim() || !labels?.length) return [];
  const ordered = labels.slice().sort((a, b) => b.length - a.length);
  const finder = new RegExp(`(^|\\n)\\s*(${ordered.map(escapeRegExp).join('|')})\\s*:\\s*`, 'gi');
  const matches = [...prepared.matchAll(finder)];
  if (!matches.length) return [];
  return matches.map((match, idx) => {
    const start = match.index + match[0].length;
    const end = idx + 1 < matches.length ? matches[idx + 1].index : prepared.length;
    return {
      heading: match[2].replace(/:\s*$/, '').trim(),
      content: prepared.slice(start, end).trim(),
    };
  });
}

function extractProfilePair(text) {
  const direct = String(text || '').match(/(?:PROFILE:\s*)?([DISC])\s*\/\s*([DISC?])/i);
  if (direct) return `${direct[1].toUpperCase()}/${direct[2].toUpperCase()}`;
  const letterFrom = (line) => {
    if (!line) return null;
    if (/\b(D|Dominance)\b/i.test(line)) return 'D';
    if (/\b(I|Influence)\b/i.test(line)) return 'I';
    if (/\b(S|Steadiness)\b/i.test(line)) return 'S';
    if (/\b(C|Conscientiousness)\b/i.test(line)) return 'C';
    return null;
  };
  const primary = letterFrom(String(text || '').match(/DISC PRIMARY STYLE[:\s]*\n*([^\n]+)/i)?.[1]);
  const secondary = letterFrom(String(text || '').match(/DISC SECONDARY STYLE[:\s]*\n*([^\n]+)/i)?.[1]);
  if (primary && secondary) return `${primary}/${secondary}`;
  if (primary) return primary;
  return '';
}

export function parseDiscRows(text) {
  const prepared = String(text || '').replace(/\r\n/g, '\n');
  const rows = [];
  const headingAlt = DISC_SECTION_HEADINGS.map(escapeRegExp).join('|');
  const splitter = new RegExp(`(^|\\n)\\s*(${headingAlt})\\s*:?\\s*(?=\\n|$)`, 'gi');
  const matches = [...prepared.matchAll(splitter)];
  const parsedHeadings = [];
  if (matches.length) {
    matches.forEach((match, idx) => {
      const start = match.index + match[0].length;
      const end = idx + 1 < matches.length ? matches[idx + 1].index : prepared.length;
      const rawHeading = match[2].replace(/:\s*$/, '').trim();
      const content = prepared.slice(start, end).trim();
      if (!content) return;
      parsedHeadings.push({
        raw: rawHeading.toUpperCase(),
        heading: DISC_HEADING_ALIASES[rawHeading.toUpperCase()] || rawHeading,
        content,
      });
    });
  }

  const rank = parsedHeadings.find((item) => item.raw === 'DISC RANK');
  const profile = (rank?.content || extractProfilePair(prepared) || '').trim();
  if (profile) {
    rows.push({ heading: 'Profile', content: profile, source: 'Social Media Analysis' });
  }
  parsedHeadings.forEach((item) => {
    if (item.raw === 'DISC RANK') return;
    rows.push({
      heading: item.heading,
      content: item.content,
      source: 'Social Media Analysis',
    });
  });

  if (rows.length <= (profile ? 1 : 0)) {
    const leftover = prepared.replace(/PROFILE:\s*[DISC]\/[DISC?]/i, '').trim();
    if (leftover) {
      rows.push({ heading: 'DISC Analysis', content: leftover, source: 'Social Media Analysis' });
    }
  }
  return rows;
}

export function parsePlaybookRunSheet(text) {
  const prepared = String(text || '').replace(/\r\n/g, '\n');
  const matches = [...prepared.matchAll(/(?:^|\n)\s*Step\s+(\d+)\s*:\s*([^\n]*)/gi)];
  if (!matches.length) return [];
  return matches.map((match, idx) => {
    const start = match.index + match[0].length;
    const end = idx + 1 < matches.length ? matches[idx + 1].index : prepared.length;
    const block = `${(match[2] || '').trim()}\n${prepared.slice(start, end)}`.trim();
    const take = (label) => {
      const re = new RegExp(
        `(?:^|\\n)\\s*${label}\\s*:\\s*([\\s\\S]*?)(?=(?:\\n\\s*(?:Do|Say|If this step fails|Channel|Timing)\\s*:)|$)`,
        'i',
      );
      return block.match(re)?.[1]?.trim() || '';
    };
    const title = (match[2] || '').trim();
    const doText = take('Do');
    const sayText = take('Say');
    const failText = take('If this step fails');
    const whatYouDo = [title && !/^do$/i.test(title) ? title : '', doText, failText && `If this step fails: ${failText}`]
      .filter(Boolean)
      .join('\n');
    return {
      step_number: match[1],
      what_you_do: whatYouDo || title || `Step ${match[1]}`,
      what_you_say: sayText,
    };
  });
}

export function parseTargetRowsFromText(text) {
  const rows = [];
  let current = null;
  const pushCurrent = () => {
    if (current) rows.push(current);
    current = null;
  };

  String(text || '')
    .split('\n')
    .forEach((raw) => {
      const line = raw.trim();
      if (!line || /^target selection$/i.test(line) || /^elicitation targets?$/i.test(line)) return;
      const named = line.match(/^Target:\s+(.+)$/i);
      if (named) {
        pushCurrent();
        current = {
          num: String(rows.length + 1),
          name: named[1].trim(),
          role: '',
          relationship: '',
          angle: '',
          evidence: '',
          risk: '',
        };
        return;
      }
      const numbered = line.match(/^(\d+)[\.)]\s+(.+)$/);
      if (numbered) {
        pushCurrent();
        let name = numbered[2].trim();
        let angle = '';
        const dash = name.match(/^(.+?)\s+[\u2014\-–—]\s+(.+)$/);
        if (dash) {
          name = dash[1].trim();
          angle = dash[2].trim();
        }
        current = {
          num: numbered[1],
          name,
          role: '',
          relationship: '',
          angle,
          evidence: '',
          risk: '',
        };
        return;
      }
      const kv = line.match(
        /^(Role|Relationship(?: to Subject)?|Angle|Evidence|Risk|Why Strong Source|Why this target|Source|Access|Primary Risks)\s*:\s*(.*)$/i,
      );
      if (kv && current) {
        const key = kv[1].toLowerCase();
        const value = kv[2].trim();
        if (key.startsWith('role')) current.role = value;
        else if (key.startsWith('relationship')) current.relationship = value;
        else if (key.startsWith('evidence') || key === 'source') current.evidence = value || current.evidence;
        else if (key.includes('risk')) current.risk = value;
        else current.angle = value || current.angle;
      }
    });
  pushCurrent();
  return rows;
}

function parseProseRows(heading, text, { numbered = false } = {}) {
  const paragraphs = String(text || '')
    .split(/\n\s*\n/)
    .map((item) => item.trim())
    .filter(Boolean);
  if (!paragraphs.length) {
    return [{ heading, content: '', source: 'Analysis' }];
  }
  if (paragraphs.length === 1) {
    return [{ heading, content: paragraphs[0], source: 'Analysis' }];
  }
  return paragraphs.map((content, idx) => ({
    heading: numbered ? `Paragraph ${idx + 1}` : idx === 0 ? heading : `${heading} (${idx + 1})`,
    content,
    source: 'Analysis',
  }));
}

function parseOverviewRows(heading, text) {
  const paragraphs = String(text || '')
    .split(/\n\s*\n/)
    .map((item) => item.trim())
    .filter(Boolean);
  if (paragraphs.length <= 1) {
    return [{ heading, content: String(text || '').trim(), source: 'Analysis' }];
  }
  const rows = [{ heading: 'Subject', content: paragraphs[0], source: 'Analysis' }];
  if (paragraphs[1]) {
    rows.push({ heading: 'What was analyzed', content: paragraphs[1], source: 'Analysis' });
  }
  const rest = paragraphs.slice(2).join('\n\n').trim();
  if (rest) {
    rows.push({ heading: 'Scope and operational picture', content: rest, source: 'Analysis' });
  }
  return rows;
}

function targetRowsToCards(targets) {
  return targets.map((target) => {
    const lines = [
      target.angle ? `Angle: ${target.angle}` : '',
      target.evidence ? `Evidence: ${target.evidence}` : '',
      target.risk ? `Risk: ${target.risk}` : '',
      target.role ? `Role: ${target.role}` : '',
      target.relationship ? `Relationship: ${target.relationship}` : '',
    ]
      .filter(Boolean)
      .join('\n');
    return {
      heading: `${target.num}. ${target.name}`,
      content: lines,
      source: 'Dossier',
    };
  });
}

export function parseSovereignHtml(html) {
  const raw = String(html || '');
  if (!raw.trim()) return { sections: [] };
  const sectionRe =
    /(?:<a class="toc-target"[^>]*><\/a>\s*)?<section class="report-section"[^>]*>\s*<h2[^>]*><strong>(.*?)<\/strong><\/h2>\s*<div class="prose">([\s\S]*?)<\/div>\s*<\/section>/gi;
  const sections = [];
  for (const match of raw.matchAll(sectionRe)) {
    const heading = decodeEntities(match[1].replace(/<[^>]+>/g, '')).trim();
    const bodyHtml = match[2] || '';
    const text = htmlFragmentToPlain(bodyHtml);
    const lower = heading.toLowerCase();
    if (lower.includes('communication profile') || (lower.includes('disc') && !lower.includes('language'))) {
      const rows = parseDiscRows(text);
      sections.push({
        title: heading,
        rows: rows.length ? rows : [{ heading: 'DISC Analysis', content: text, source: 'Social Media Analysis' }],
        playbook: [],
        rowCount: Math.max(rows.length, 1),
      });
      continue;
    }
    if (lower.includes('target selection') || lower.includes('elicitation target')) {
      const targets = parseTargetRowsFromText(text);
      const rows = targets.length
        ? targetRowsToCards(targets)
        : [{ heading, content: text, source: 'Dossier' }];
      sections.push({
        title: heading,
        rows,
        targets,
        playbook: [],
        rowCount: rows.length,
      });
      continue;
    }
    if (lower.startsWith('pretext') || lower.startsWith('playbook')) {
      const fields = splitLabeledFields(text, PLAYBOOK_FIELD_LABELS).filter((item) => item.content);
      const playbook = parsePlaybookRunSheet(text);
      const rows = fields.length
        ? fields
            .filter((item) => !(playbook.length && /^execution steps$/i.test(item.heading)))
            .map((item) => ({
              heading: item.heading,
              content: item.content,
              source: item.heading.toLowerCase().includes('source') ? 'Dossier' : 'Analysis',
            }))
        : parseProseRows(heading, text);
      sections.push({
        title: heading,
        rows,
        playbook,
        rowCount: Math.max(rows.length, playbook.length),
      });
      continue;
    }
    if (lower === 'overview' || lower.startsWith('overview')) {
      const rows = parseOverviewRows(heading, text);
      sections.push({ title: heading, rows, playbook: [], rowCount: rows.length });
      continue;
    }
    if (lower.includes('executive summary')) {
      const paragraphs = String(text || '')
        .split(/\n\s*\n/)
        .map((item) => item.trim())
        .filter(Boolean);
      sections.push({
        title: heading,
        kind: 'prose',
        paragraphs: paragraphs.length ? paragraphs : [String(text || '').trim()].filter(Boolean),
        rows: [],
        playbook: [],
        rowCount: Math.max(paragraphs.length, 1),
      });
      continue;
    }
    if (lower === 'evidence ledger') {
      const files = String(text || '')
        .split('\n')
        .map((line) => line.replace(/^[-*•]\s*/, '').trim())
        .filter((line) => line && !/^no files were provided/i.test(line));
      sections.push({
        title: heading,
        kind: 'file-list',
        files,
        rows: [],
        playbook: [],
        rowCount: files.length,
      });
      continue;
    }
    const rows = parseProseRows(heading, text);
    sections.push({
      title: heading,
      rows,
      playbook: [],
      rowCount: rows.length,
    });
  }
  return { sections };
}
